import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { DELETE_CONTENT, DELETE_REPORT, GET_BLOG_DETAILS } from "../../../api/url";
import Loader from "../../../components/common/Loader";
import { arrowBack, deleteIcon } from "../../../assets/image";
import DeletModel from "../../model/DeletModel";
import DissmissReportModel from "../../../components/ReportComponents/DissmissReportModel";
import moment from "moment";
import { string } from "yup";
import { strings } from "../../../strings/strings";
import { toast } from "react-toastify";

const BlogDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromReport = location?.state?.fromReport || false; // Check if you're coming from a report page
  const report = location?.state?.report || null; // Get the report details from state

  const [results, setResults] = useState({});
  const [selectedBlogId, setSelectedBlogId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [showDismissModal, setShowDismissModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const { mutate: deleteBlog } = useAppMutation();
  const { mutate: dismissReport } = useAppMutation();

  const { data, isLoading, refetch } = useAppQuery({
    queryKey: [QUERY_KEYS.BLOG_DETAILS, location?.state?.blogId],
    url: `${GET_BLOG_DETAILS}?blogId=${location.state?.blogId}`,
  });

  useEffect(() => {
    if (data) {
      setResults(data?.results);
    }
  }, [data]);

  const handleDeleteBlog = (blog) => {
    const contentId = blog?.id || blog?._id || blog?.content?.id;
    if (!contentId) {
      toast.error(strings.invalidBlogId);
      return;
    }
    setSelectedBlogId(contentId);
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedBlogId) {
      toast.error(strings.noBlogIdSelected);
      return;
    }

    deleteBlog(
      {
        url: `${DELETE_CONTENT}?contentId=${selectedBlogId}`,
        method: "DELETE",
      },
      {
        onSuccess: () => {
          toast.success(strings.deleteBlogSucess);
          setShowModal(false); // Hide the modal after successful deletion
          setSelectedBlogId(null); // Reset the selected blog ID
          setTimeout(() => navigate(-1), 1000);
        },
        onError: () => {
          toast.error(strings.failToDeleteBlog);
        },
      }
    );
  };

  const handleCancelDelete = () => {
    setShowModal(false);
    setSelectedBlogId(null);
  };

  const handleDismissReport = (report) => {
    if (!report?._id) {
      toast.error(strings.noReportIdAvailableForDissmiss);
      return;
    }

    setSelectedReportId(report?._id); // Set the report ID to dismiss
    setShowDismissModal(true);
  };

  const handleConfirmDismiss = () => {
    if (!selectedReportId) {
      toast.error(strings.NoReportSelected);
      return;
    }

    dismissReport(
      {
        url: `${DELETE_REPORT}?reportId=${selectedReportId}`,
        method: "DELETE",
      },
      {
        onSuccess: () => {
          toast.success(strings.reportDissmissSuccess);
          setShowDismissModal(false);
          setSelectedReportId(null);
          refetch();
          setTimeout(() => navigate(-1), 1000);
        },
        onError: () => {
          toast.error(strings.failToDissmissReport);
        },
      }
    );
  };

  const handleCancelDismiss = () => {
    setShowDismissModal(false);
    setSelectedReportId(null);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="flex gap-2 mb-5">
        <button onClick={() => navigate(-1)}>
          <img src={arrowBack} alt="Back" />
        </button>
        <h2 className="text-xl font-semibold">Blog Details</h2>
      </div>

      <div className="max-w-4xl mx-auto rounded-xl p-4">
        <div className="flex justify-between items-center gap-5 mb-3">
          <h1 className="text-xl mb-3 font-bold">{results?.tittle}</h1>
          {fromReport ? (
            <div className="flex gap-3">
              <button onClick={() => handleDismissReport(report)} className="text-primary font-semibold">
                {strings?.dismissReport}
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={() => handleDeleteBlog(results)} className="text-red-600 font-semibold">
                {strings?.deleteBlog}
              </button>
            </div>
          ) : (
            <button onClick={() => handleDeleteBlog(results)}>
              <img src={deleteIcon} alt="Delete" className="w-6 h-6" />
            </button>
          )}
        </div>

        <div>
          <img src={results?.blogImage} alt="Blog" className="w-full h-full object-cover rounded-lg" />
          <div className="flex gap-3 items-center my-6">
            <img src={results?.user?.profileImage} alt={results?.user?.userName} className="w-12 h-12 rounded-full" />
            <div className="grid">
              <p className="font-semibold">{results?.user?.userName}</p>
              <p>{moment(results?.createdAt).fromNow()}</p>
            </div>
          </div>
        </div>

        <div>
          <div className="prose -invert max-w-none" dangerouslySetInnerHTML={{ __html: results?.artical }} />
        </div>
      </div>

      {showModal && <DeletModel handleConfirmDelete={handleConfirmDelete} handleCancelDelete={handleCancelDelete} />}

      {showDismissModal && (
        <DissmissReportModel handleconfirmDissmiss={handleConfirmDismiss} handleCancelDissmiss={handleCancelDismiss} />
      )}
    </div>
  );
};

export default BlogDetails;
